import type { ReactNode } from 'react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useEngagementMetricsData } from '../../../hooks/useEngagementMetricsData';
import { ErrorState } from '../../analytics/shared/ErrorState';
import { EngagementMetricsKpiTiles } from '../overview/EngagementMetricsKpiTiles';
import { EngagementMetricsTrendChart } from '../overview/EngagementMetricsTrendChart';
import { EngagementTypeAndBillableCharts } from '../overview/EngagementTypeAndBillableCharts';
import { ProviderCategoryBreakdowns } from '../overview/ProviderCategoryBreakdowns';
import { MessageAndReferralBreakdowns } from '../overview/MessageAndReferralBreakdowns';
import { EngagementFunnelCharts } from '../overview/EngagementFunnelCharts';
import { TopSpecialtiesBreakdown } from '../overview/TopSpecialtiesBreakdown';
import { ProvidersNeedingAttentionTable } from '../overview/ProvidersNeedingAttentionTable';
import type { EngagementMetricsQueryParams } from '../../../types/engagement-metrics.types';

type EngagementMetricsOverviewTabProps = {
  queryParams: EngagementMetricsQueryParams;
  isActive?: boolean;
};

/** Swaps in `skeleton` while loading, `children` once data resolves — used once per section below. */
const MetricSection = ({
  isLoading,
  skeleton,
  children,
}: {
  isLoading: boolean;
  skeleton: ReactNode;
  children: ReactNode;
}) => <>{isLoading ? skeleton : children}</>;

const TwoUpSkeleton = ({ heightClass }: { heightClass: string }) => (
  <div className="grid grid-cols-2 gap-3.5">
    <Skeleton className={heightClass} />
    <Skeleton className={heightClass} />
  </div>
);

/**
 * Overview sub-tab: KPI, trend, type/billable, provider, and category sections
 * are wired to the shared filtered endpoint.
 */
export const EngagementMetricsOverviewTab = ({
  queryParams,
  isActive = true,
}: EngagementMetricsOverviewTabProps) => {
  const { data, isLoading, isError, refetch } = useEngagementMetricsData(
    queryParams,
    isActive,
  );

  if (!isActive) return null;

  if (isError) {
    return (
      <div className="pt-6" data-testid="engagement-metrics-overview-tab">
        <ErrorState
          message="Failed to load engagement metrics. Please try again."
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  const loading = isLoading || !data;

  return (
    <div className="space-y-6 pt-6" data-testid="engagement-metrics-overview-tab">
      <MetricSection
        isLoading={loading}
        skeleton={
          <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        }
      >
        {data && <EngagementMetricsKpiTiles kpis={data.kpis} />}
      </MetricSection>

      <MetricSection isLoading={loading} skeleton={<Skeleton className="h-64 w-full rounded-xl" />}>
        {data && <EngagementMetricsTrendChart data={data.trend} />}
      </MetricSection>

      <MetricSection isLoading={loading} skeleton={<TwoUpSkeleton heightClass="h-80 rounded-xl" />}>
        {data && (
          <EngagementTypeAndBillableCharts byType={data.byType} billableSplit={data.billableSplit} />
        )}
      </MetricSection>

      <MetricSection isLoading={loading} skeleton={<TwoUpSkeleton heightClass="h-80 rounded-xl" />}>
        {data && (
          <ProviderCategoryBreakdowns topProviders={data.topProviders} byCategory={data.byCategory} />
        )}
      </MetricSection>

      <MetricSection isLoading={loading} skeleton={<TwoUpSkeleton heightClass="h-72 rounded-xl" />}>
        {data && (
          <MessageAndReferralBreakdowns
            byFpSpMessageType={data.byFpSpMessageType}
            referralBreakdown={data.referralBreakdown}
          />
        )}
      </MetricSection>

      <MetricSection isLoading={loading} skeleton={<TwoUpSkeleton heightClass="h-72 rounded-xl" />}>
        {data && (
          <EngagementFunnelCharts
            profileViewFunnel={data.profileViewFunnel}
            engagementFunnel={data.engagementFunnel}
          />
        )}
      </MetricSection>

      <MetricSection isLoading={loading} skeleton={<Skeleton className="h-80 rounded-xl" />}>
        {data && <TopSpecialtiesBreakdown topSpecialties={data.topSpecialties} />}
      </MetricSection>

      <MetricSection isLoading={loading} skeleton={<Skeleton className="h-80 rounded-xl" />}>
        {data && <ProvidersNeedingAttentionTable queryParams={queryParams} />}
      </MetricSection>
    </div>
  );
};
